<script setup lang="ts">
import { type PropType, ref } from 'vue';
import { useRouter } from 'vue-router';
import Select, { type SelectChangeEvent } from 'primevue/select';
import {
  ButtonType,
  CustomButton
} from '@/components';
import { CaseModel, EntryModel } from '@/models';
import { useCaseStore } from '@/stores/case';
import { useEntryListStore } from '@/stores/entryList';
import { routeConstants } from '@/utils';

const entryStore = useEntryListStore();
const caseStore = useCaseStore();

const props = defineProps({
  entryModel: {
    type: Object as PropType<EntryModel>,
    required: true
  },
  caseModel: {
    type: Object as PropType<CaseModel>
  }
});

const router = useRouter();
const onCaseClick = () => {
  entryStore.resetEntryList(props.caseModel);
  router.push({
    name: routeConstants.CASE.name,
    params: {
      identifier: props.caseModel?.identifier
    }
  });
};

const editMode = ref(false);
const selectedCase = ref(props.caseModel);

const onEditClick = () => {
  editMode.value = !editMode.value;
};

const emits = defineEmits(['onCaseChanged']);

const onSelectChange = (event: SelectChangeEvent) => {
  emits('onCaseChanged', props.entryModel, event.value);
  editMode.value = !editMode.value;
};
</script>

<template>
    <div class="case-select-root">
        <div class="case-button" v-if="!editMode">
            <div class="no-case-label" v-if="!caseModel">No case</div>
            <CustomButton :text="caseModel?.label"
                @onClick="onCaseClick"
                v-if="caseModel"
                :type="ButtonType.PRIMARY_WITHOUT_OUTLINE"/>
            <FontAwesomeIcon :icon="['fal', 'pen-to-square']"
                class="edit-icon"
                @click.stop="onEditClick"
                v-ripple/>
        </div>
        <div class="case-select" v-if="editMode">
            <Select v-model="selectedCase"
                class="case-select-input"
                optionLabel="label"
                filter
                showClear
                @click.stop
                :options="caseStore.studyCaseList"
                @change="onSelectChange"/>
            <CustomButton text="Cancel"
                @onClick.stop="onEditClick"
                :marginLeft="12"
                :type="ButtonType.WITHOUT_OUTLINE"/>
        </div>
    </div>
</template>

<style scoped lang="scss">
.edit-icon {
    margin-left: 12px;
    visibility: hidden;
}
.no-case-label {
  padding: 0px 12px;
}
.case-button {
    display: flex;
    align-items: center;

    &:hover {
        .edit-icon {
            visibility: visible;
        }
    }
}
.case-select {
    display: flex;
    align-items: center;

    .case-select-input {
        max-width: 150px;
    }
}
</style>
